/* eslint-disable react/display-name */
import React, { useEffect, useState, useRef, useMemo } from "react";
import styled from "styled-components";
import _ from "lodash";

import { app } from "js/namespaces";
import { stopPropagation } from "js/core/utilities/stopPropagation";
import PresentationEditorController from "js/editor/PresentationEditor/PresentationEditorController";
import { PropertyPanelContainer, PropertySection } from "js/EditorComponents/PropertyPanel";
import { PropertyPanelHeader } from "js/editor/ElementEditor/PropertyPanelHeader";
import { themeColors } from "js/react/sharedStyles";
import { Button } from "js/Components/Button";
import { replaceTextInHtml, removeMarkNodes, deleteAndReplace } from "js/core/utilities/htmlTextHelpers";
import { Key } from "js/core/utilities/keys";
import { ShowConfirmationDialog, ShowDialog } from "js/react/components/Dialogs/BaseDialog";
import ProgressDialog from "js/react/components/Dialogs/ProgressDialog";
import { FlexSpacer } from "js/react/components/Gap";
import { IconButton } from "js/Components/IconButton";

import SearchResults from "./SearchResults";
import getLogger, { LogGroup } from "js/core/logger";
import Spinner from "js/react/components/Spinner";
import { LockShield } from "js/editor/ElementEditor/EditorPropertyPanel";

const HIGHLIGHT_SELECTED_CLASS = "highlighted-selected-text";

const logger = getLogger(LogGroup.editor);

const regexPattern = /[.*+?^${}()|[\]\\]/g;

// Modify the PropertyPanelContainer to use flex layout
const FlexPropertyPanelContainer = styled(PropertyPanelContainer)`
    display: flex;
    flex-direction: column;
    height: 100%;
`;

const FineAndReplaceText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const MatchesLabel = styled.label`
    font-family: "Source Sans Pro";
    font-size: 11px;
    font-style: normal;
    font-weight: 600;
    line-height: 14px; /* 127.273% */
    text-transform: uppercase;
    color: #222;
    flex: 1;
`;

const FocusedInput = styled.input`
    font-family: "Source Sans Pro";
    font-size: 12px;       
    font-weight: 400;
    line-height: normal;

    display: flex;
    padding: 7px;
    align-items: center;
    align-self: stretch;
    caret-color: #00ACE8;

    border: 1px solid #ccc;

    &::placeholder{
        font-style: normal;
    }

    &:focus {
        box-shadow: 0px 0px 0px 3px rgba(17, 169, 226, 0.30);
        border: 1px solid ${themeColors.ui_blue};
        border-radius: 2px;
        outline: none;
    }
`;

const MatchesContainer = styled.div`
    display: flex;
    padding: 9px 0;
    align-items: center;
    gap: 9px;
    align-self: stretch;
    background: #F9F9F9;
`;

const FineAndReplaceButttons = styled.div`
    display: flex;
    flex-direction: row;
    gap: 10px;
`;

const StyledButton = styled(Button)`
    color: #222;
    font-size: 12px;
    font-family: "Source Sans Pro";
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.50px;
    word-wrap: break-word;
    flex: 1;
    justify-content: center;
`;

// Create a flex container for the results section
const ResultsContainer = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0; /* This is crucial for enabling flex child to scroll */
    width: 100%;
`;

const highlightText = (slidesTextModel, query, indexInSlide) => {
    removeMarkNodes();

    // No query, just exit after clearing highlights
    if (!query) return 0;

    // Get current slide elements
    const canvas = PresentationEditorController.currentCanvasController.canvas;
    const items = slidesTextModel.filter(slideTextModel => slideTextModel.slideIndex === canvas.getSlideIndex());
    const blockRefs = items.map(item => item.blockRef);

    // Create an optimized regex for the search
    const escapedQuery = query.replace(regexPattern, "\\$&");
    const regex = new RegExp(escapedQuery, "gi");

    // Track matches for indexing
    let matchCounter = 0;

    // Process each block reference
    blockRefs.forEach(blockRef => {
        // First create a clone to work with to avoid affecting the original during search
        const clonedBlock = blockRef.cloneNode(true);
        const fullText = clonedBlock.textContent;

        // Find all matches in the full text
        regex.lastIndex = 0;
        let match;
        const matches = [];
        while ((match = regex.exec(fullText)) !== null) {
            matches.push({
                index: match.index,
                length: match[0].length,
                text: match[0]
            });
        }

        if (matches.length === 0) return;

        // Now work with the actual DOM to highlight matches
        const textNodeMapping = [];
        mapTextNodes(blockRef, textNodeMapping, 0);

        // Process matches in reverse order to avoid index shifting
        for (let i = matches.length - 1; i >= 0; i--) {
            const match = matches[i];
            const currentMatchIndex = matchCounter + i;
            const isSelected = (indexInSlide !== undefined && indexInSlide === currentMatchIndex);

            highlightMatchInNodes(textNodeMapping, match.index, match.length, isSelected);
        }

        matchCounter += matches.length;
    });

    return matchCounter;
};

// Helper function to map text nodes to their positions in the full text
function mapTextNodes(node, mapping, offset) {
    if (node.nodeType === Node.TEXT_NODE) {
        const length = node.textContent.length;
        if (length > 0) {
            mapping.push({
                node: node,
                start: offset,
                end: offset + length - 1
            });
        }
        return offset + length;
    }

    // Skip script and style tags
    if (node.tagName === "SCRIPT" || node.tagName === "STYLE") {
        return offset;
    }

    let currentOffset = offset;
    // Process child nodes
    node.childNodes.forEach(child => {
        currentOffset = mapTextNodes(child, mapping, currentOffset);
    });

    return currentOffset;
}

// Highlight a match that may span across multiple text nodes
function highlightMatchInNodes(mapping, startIndex, length, isSelected) {
    const endIndex = startIndex + length - 1;

    // Find nodes that contain any part of the match
    const relevantNodes = mapping.filter(item =>
        !(item.end < startIndex || item.start > endIndex)
    ).sort((a, b) => a.start - b.start);

    if (!relevantNodes.length) return;

    // Special case: match entirely within one text node
    if (relevantNodes.length === 1) {
        const nodeInfo = relevantNodes[0];
        const relativeStart = startIndex - nodeInfo.start;
        const relativeEnd = relativeStart + length;

        try {
            const range = document.createRange();
            range.setStart(nodeInfo.node, relativeStart);
            range.setEnd(nodeInfo.node, relativeEnd);

            const mark = document.createElement("mark");
            if (isSelected) mark.classList.add(HIGHLIGHT_SELECTED_CLASS);

            range.surroundContents(mark);
        } catch (err) {
            logger.error(err, "[SearchBarPanel] Failed to highlight match in node");
        }
        return;
    }

    // Handle multi-node matches
    for (let i = 0; i < relevantNodes.length; i++) {
        const nodeInfo = relevantNodes[i];
        let relativeStart, relativeEnd;

        // First node: highlight from match start to node end
        if (i === 0) {
            relativeStart = Math.max(0, startIndex - nodeInfo.start);
            relativeEnd = nodeInfo.node.textContent.length;
        } else if (i === relevantNodes.length - 1) {
            // Last node: highlight from node start to match end
            relativeStart = 0;
            relativeEnd = Math.min(endIndex - nodeInfo.start + 1, nodeInfo.node.textContent.length);
        } else {
            // Middle nodes: highlight entire content
            relativeStart = 0;
            relativeEnd = nodeInfo.node.textContent.length;
        }

        try {
            const range = document.createRange();
            range.setStart(nodeInfo.node, relativeStart);
            range.setEnd(nodeInfo.node, relativeEnd);

            const mark = document.createElement("mark");
            if (isSelected) mark.classList.add(HIGHLIGHT_SELECTED_CLASS);

            range.surroundContents(mark);
        } catch (err) {
            logger.error(err, "[SearchBarPanel] Failed to highlight part of multi-node match");
        }
    }
}

const SearchBarPanel = ({ currentCanvasController,
    hideShowBar,
    onMouseDown,
    onMouseLeave,
    currentSlide,
    isCurrentSlideLockedForMe }) => {
    const [slidesTextModel, setSlideTextModel] = useState([]);
    const [findTextResults, setFindTextResults] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const findInputRef = useRef(null);
    const replaceInputRef = useRef(null);
    const seachResultsRef = useRef(null);
    const [disableReplaceButtons, setDisableReplaceButtons] = useState(true);
    const [isResultsReady, setIsResultsReady] = useState(false);

    const handleKeyDown = useMemo(() => {
        return async event => {
            switch (event.which) {
                case Key.DOWN_ARROW:
                case Key.UP_ARROW: {
                    // Persist the event to prevent React from nullifying it after async operations
                    // Check if persist exists since React 17+ removed event pooling
                    // This is to ensure that the event is not nullified
                    // and we can safely call persist on it
                    // https://reactjs.org/docs/events.html#event-pooling
                    // Once we update to React 17+, we can remove this check
                    if (typeof event.persist === "function") {
                        event.persist();
                    }
                    event.preventDefault();

                    stopPropagation()(event);

                    // Exit early if there are no results
                    if (findTextResults.length === 0) {
                        break;
                    }

                    const direction = event.which === Key.DOWN_ARROW ? 1 : -1;

                    // Calculate the next index with proper modulo handling
                    const nextIndex = ((selectedIndex + direction) + findTextResults.length) % findTextResults.length;

                    // Only navigate to a different slide if needed
                    const currentResultIndex = selectedIndex >= 0 ? selectedIndex % findTextResults.length : 0;
                    if (findTextResults[nextIndex]?.index !== findTextResults[currentResultIndex]?.index || currentSlide.getIndex() !== findTextResults[nextIndex]?.index) {
                        await PresentationEditorController.setCurrentSlideByIndex(findTextResults[nextIndex].index);
                    }

                    // Update the selected index
                    setSelectedIndex(nextIndex);

                    // Highlight the text for the new selection
                    highlightText(slidesTextModel, findInputRef.current.value, findTextResults[nextIndex].counter);
                    _.defer(() => findInputRef.current.focus());
                    break;
                }
                case Key.ESCAPE: {
                    closePanel();
                    break;
                }
            }

            // make sure to prevent default behavior for the following key combinations
            if (event.metaKey || event.ctrlKey) {
                switch (event.which) {
                    case Key.KEY_F:
                        event.stopPropagation();
                        event.preventDefault();
                        return;
                }
            }
        };
    }, [selectedIndex]);

    useEffect(() => {
        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [handleKeyDown]);

    // Load text data on mount
    useEffect(() => {
        loadTextForAllSlides();
        _.defer(() => findInputRef.current.focus({ preventScroll: true }));
    }, []);

    const loadTextForAllSlides = () => {
        setIsResultsReady(false);
        PresentationEditorController.getTextForAllSlides().then(res => {
            setIsResultsReady(true);
            setSlideTextModel(res);
        });
    };

    // Set the initial selected value correctly within the results
    useEffect(() => {
        if (findInputRef.current.value === "") {
            return;
        }

        if (findTextResults.length > 0 && selectedIndex === -1) {
            // || previousSlideId.current !== currentSlide.id)) {
            const slideIndex = PresentationEditorController.getCurrentSlideIndex();

            // find current slide index in results
            setSelectedIndex(findTextResults.findIndex(result => {
                // return current or the next available slide index
                return result.index >= slideIndex;
            }));

            highlightText(slidesTextModel, findInputRef.current.value, 0);
        }
    }, [findTextResults]);

    // Refresh results when the slides text model changes
    useEffect(() => {
        if (findInputRef.current.value) {
            refreshResults(findInputRef.current.value);
        }
    }, [slidesTextModel]);

    // Handle focus/blur based on slide type
    useEffect(() => {
        if (currentSlide.isLibrarySlide()) {
            findInputRef.current.blur();
            replaceInputRef.current.blur();
        } else {
            findInputRef.current.focus({ preventScroll: true });
        }
    }, [currentSlide]);

    const refreshResults = value => {
        // if empty string, clear results
        if (value === "") {
            setFindTextResults([]);
            _.defer(() => findInputRef.current.focus());
            return;
        }

        // Early exit for empty search
        if (!value) {
            return [];
        }

        // Compile regex once outside loops - use exact string matching (no space transformation)
        const escapedValue = value.replace(regexPattern, "\\$&");
        const regex = new RegExp(escapedValue, "gi");
        const textResults = [];
        let currentSlideIndex = -1;
        let counter = 0;

        // Process all slides in a single loop
        for (const slide of slidesTextModel) {
            const text = slide.text || "";
            if (!text) continue;

            // Reset counter when slide changes
            if (currentSlideIndex !== slide.slideIndex) {
                currentSlideIndex = slide.slideIndex;
                counter = 0;
            }

            // Reset regex for each text search
            regex.lastIndex = 0;

            // Find all matches
            let match;
            while ((match = regex.exec(text)) !== null) {
                const startIndex = match.index;
                const matchLength = match[0].length;

                // Pre-calculate text bounds for context
                const start = Math.max(startIndex - 30, 0);
                const end = Math.min(startIndex + matchLength + 30, text.length);

                // Add result directly to array
                textResults.push({
                    matchIndex: startIndex,
                    start: startIndex,
                    end: startIndex + matchLength,
                    counter: counter++,
                    index: slide.slideIndex,
                    before: text.substring(start, startIndex),
                    match: match[0],
                    after: text.substring(startIndex + matchLength, end)
                });
            }
        }

        setFindTextResults(textResults);

        _.defer(() => {
            if (findInputRef.current) {
                findInputRef.current.focus();
            }
        });
    };

    const handleChange = event => {
        const value = event.target.value;
        refreshResults(value);
        setSelectedIndex(-1);
        highlightText(slidesTextModel, value);
    };

    const handleReplace = async () => {
        const canvas = currentCanvasController.canvas;

        const textModels = slidesTextModel.filter(slideTextModel => slideTextModel.slideIndex === canvas.getSlideIndex());

        let targetElement;
        let matchCounter = 0;
        for (const textModel of textModels) {
            const regex = new RegExp(findInputRef.current.value.replace(regexPattern, "\\$&").replace(/\s/g, "(\\s|&nbsp;)"), "gi");
            const matches = [...textModel.blockRef.textContent.matchAll(regex)];

            for (const match of matches) {
                if (matchCounter === findTextResults[selectedIndex].counter) {
                    targetElement = textModel;
                    break;
                }
                matchCounter++;
            }
            if (targetElement) break;
        }

        removeMarkNodes();
        if (canvas.getElementByUniquePath(targetElement.elementPath).type === "Table") {
            const table = canvas.getElementByUniquePath(targetElement.elementPath);
            const cellText = table.cells[targetElement.cellIndex].model?.cellText?.text ?? "";
            table.cells[targetElement.cellIndex].model.cellText.text = cellText
                .replace(findInputRef.current.value, replaceInputRef.current.value);
            await table.updateModel();
        } else {
            const replacedContent = replaceTextInHtml(targetElement.blockRef.innerHTML, replaceInputRef.current.value, new RegExp(findInputRef.current.value.replace(regexPattern, "\\$&"), "gi"));
            const blockToChange = canvas.getElementByUniquePath(targetElement.elementPath).blocks.find(block => block.uid === targetElement.blockUid);
            blockToChange.model.html = replacedContent;
            await blockToChange.element.updateModel();
        }

        // We updated text that's why we want to remove the text and move to the next one
        if (findTextResults[(selectedIndex + 1) % findTextResults.length].index !== findTextResults[(selectedIndex) % findTextResults.length]?.index ?? 0) {
            await PresentationEditorController.setCurrentSlideByIndex(findTextResults[(selectedIndex + 1) % findTextResults.length].index);
        }

        await loadTextForAllSlides();

        highlightText(slidesTextModel, findInputRef.current.value, findTextResults[(selectedIndex + findTextResults.length) % findTextResults.length].counter);

        if (findTextResults[(selectedIndex + 1) % findTextResults.length].index < findTextResults[(selectedIndex) % findTextResults.length].index) {
            setSelectedIndex(0);
        } else if (findTextResults.length === 1 && selectedIndex === 1) {
            setSelectedIndex(selectedIndex - 1);
        }
    };

    const handleReplaceAll = async () => {
        const confirmed = await ShowConfirmationDialog({
            title: "Are You Sure?",
            message: `This will replace all instances of "${findInputRef.current.value}" with "${replaceInputRef.current.value}" in all of the slides.`
        });

        if (!confirmed) return;

        removeMarkNodes();

        const progressDialog = ShowDialog(ProgressDialog, {
            title: "Applying Text changes...",
            progress: 0
        });

        app.undoManager.setAllowSlideNavigation(false);
        app.undoManager.openGroup();

        await Promise.all(
            Object.values(PresentationEditorController.getCanvasControllers())
                .map(async canvasController => {
                    const canvas = canvasController.canvas;

                    const textElementsWithinCanvas = slidesTextModel.filter(slideTextModel => slideTextModel.slideIndex === canvas.getSlideIndex());

                    textElementsWithinCanvas.map(textElement => {
                        if (canvas.getElementByUniquePath(textElement.elementPath).type === "Table") {
                            const table = canvas.getElementByUniquePath(textElement.elementPath);
                            const cellText = table.cells[textElement.cellIndex].model?.cellText?.text ?? "";
                            if (cellText) {
                                // Create a regex with 'g' flag to find all instances
                                const searchRegex = new RegExp(findInputRef.current.value.replace(regexPattern, "\\$&"), "gi");

                                // Replace all occurrences with the replacement text
                                const newText = cellText.replace(searchRegex, replaceInputRef.current.value);

                                // Update the cell text
                                table.cells[textElement.cellIndex].model.cellText.text = newText;
                            }
                        } else {
                            // Create a case-insensitive regex for matching
                            const searchRegex = new RegExp(findInputRef.current.value.replace(regexPattern, "\\$&"), "gi");
                            const innerHTML = replaceTextInHtml(textElement.blockRef.innerHTML, replaceInputRef.current.value, searchRegex);
                            const blockToChange = canvas.getElementByUniquePath(textElement.elementPath).blocks.find(block => block.uid === textElement.blockUid);
                            blockToChange.model.html = innerHTML;
                        }
                    });

                    await canvasController.canvas.updateCanvasModel(false, true);
                })
        );
        app.undoManager.closeGroup();

        await loadTextForAllSlides();

        progressDialog.props.closeDialog();
    };

    const closePanel = () => {
        removeMarkNodes();
        // clear field
        replaceInputRef.current.value = "";
        findInputRef.current.value = "";
        setFindTextResults([]);
        highlightText(slidesTextModel, "");
        hideShowBar();
    };

    return (
        <LockShield
            isLocked={currentSlide.isLibrarySlide() || isCurrentSlideLockedForMe}
            onMouseDown={onMouseDown}
            onMouseLeave={onMouseLeave}
        >
            <FlexPropertyPanelContainer onKeyDown={handleKeyDown}>
                <PropertyPanelHeader callback={() => closePanel()}>
                    Find and Replace
                    <FlexSpacer />
                </PropertyPanelHeader>
                <PropertySection>
                    <FineAndReplaceText>
                        <FocusedInput type="text"
                            onChange={handleChange}
                            onClick={() => {
                                loadTextForAllSlides();
                                setSelectedIndex(-1);
                            }}
                            placeholder="Find"
                            ref={findInputRef}
                            onKeyDown={e => {
                                if (e.key === "Tab") {
                                    e.preventDefault();
                                    replaceInputRef.current.focus();
                                }
                            }}
                        />
                        <FocusedInput type="text"
                            onChange={textReplaced => setDisableReplaceButtons(textReplaced.target.value === "")}
                            id="replace-text"
                            placeholder="Replace with..."
                            ref={replaceInputRef}
                            onKeyDown={e => {
                                if (e.key === "Tab") {
                                    e.preventDefault();
                                    findInputRef.current.focus();
                                }
                            }}
                        />
                        <FineAndReplaceButttons>
                            <StyledButton disabled={disableReplaceButtons || findTextResults.length === 0} onClick={handleReplace} color="rgba(218, 233, 240, 0.5)" >Replace</StyledButton>
                            <StyledButton disabled={disableReplaceButtons || findTextResults.length === 0} onClick={handleReplaceAll} color="rgba(218, 233, 240, 0.5)" >Replace All</StyledButton>
                        </FineAndReplaceButttons>
                    </FineAndReplaceText>
                </PropertySection>
                <PropertySection>
                    <MatchesContainer>
                        <MatchesLabel>{findTextResults.length} {"Match".pluralize(findTextResults.length !== 1)}</MatchesLabel>
                    </MatchesContainer>
                </PropertySection>
                {!isResultsReady && <Spinner label="Loading results..." />}
                {isResultsReady && findTextResults?.length > 0 && <ResultsContainer>
                    <SearchResults
                        ref={seachResultsRef}
                        findTextResults={findTextResults}
                        selectedIndex={selectedIndex}
                        onClick={async (index, result) => {
                            setSelectedIndex(index);
                            await PresentationEditorController.setCurrentSlideByIndex(result.index);
                            highlightText(slidesTextModel, findInputRef.current.value, result.counter);
                        }}
                    />
                </ResultsContainer>}
            </FlexPropertyPanelContainer>
        </LockShield>
    );
};

export default PresentationEditorController.withState(SearchBarPanel);
